import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { baseUrl } from '../constant/config';

// How often to ask the server about a publish we lost track of, and for how
// long. A Facebook upload plus processing finishes well inside this.
const CHECK_EVERY_MS = 5000;
const CHECK_FOR_MS = 10 * 60 * 1000;

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

// Did the server actually answer? axios 0.27 attaches the raw browser request
// as `response` (status 0) when the network fails, so presence alone is not
// enough - an answer has an HTTP status.
const serverAnswered = (error) => Boolean(error?.response?.status);

/** Readable text for a request that got no answer at all. */
export const connectionMessage = (error) => {
  if (isOffline()) return "You're offline. Check your internet connection and try again.";
  if (error?.code === 'ECONNABORTED') return 'The connection is too slow and the request timed out. Please try again.';
  return "Couldn't reach the server. Check your internet connection and try again.";
};

/** Server's own message when it answered, otherwise a connection message. */
export const requestErrorMessage = (error) =>
  serverAnswered(error) ? error.response.data?.message || error.message : connectionMessage(error);

/**
 * Publishing a recording, made safe for a weak connection.
 *
 * The publish request stays open for the whole upload, which takes minutes.
 * If the admin's connection drops during that wait the server carries on and
 * posts anyway - so a lost connection is not a failure. Instead of saying it
 * failed (and inviting a second, duplicate upload), this asks the server how
 * the publish ended.
 */
export default function useRecordingPublish(video) {
  const queryClient = useQueryClient();
  const [publishing, setPublishing] = useState(false);
  const [checking, setChecking] = useState(false);
  const [results, setResults] = useState(null);
  const [notice, setNotice] = useState(null);
  const stopRef = useRef(null);

  const stopChecking = useCallback(() => {
    if (stopRef.current) stopRef.current();
    stopRef.current = null;
    setChecking(false);
  }, []);

  // A new recording, or the dialog reopening, starts clean.
  const reset = useCallback(() => {
    stopChecking();
    setResults(null);
    setNotice(null);
    setPublishing(false);
  }, [stopChecking]);

  useEffect(() => stopChecking, [stopChecking]);

  const checkOutcome = useCallback(
    (videoId, platforms, startedAt) => {
      stopChecking();
      setChecking(true);

      let cancelled = false;
      let timer = null;
      const deadline = Date.now() + CHECK_FOR_MS;

      const finish = (rows, message) => {
        cancelled = true;
        clearTimeout(timer);
        window.removeEventListener('online', poll);
        stopRef.current = null;
        setChecking(false);
        setNotice(message);
        if (rows) setResults(rows);
        queryClient.invalidateQueries('recordings');
      };

      async function poll() {
        if (cancelled) return;
        clearTimeout(timer);
        if (Date.now() > deadline) {
          finish(
            null,
            "Couldn't confirm whether the publish finished. Check Social Posts → Publish History before publishing again, so the video is not posted twice."
          );
          return;
        }
        if (!isOffline()) {
          try {
            const { data } = await axios.get(`${baseUrl}/api/videos/${videoId}/share-status`);
            if (cancelled) return;
            const { inProgress = [], shares = [] } = data.data || {};
            const stillRunning = platforms.some((p) => inProgress.includes(p));
            if (!stillRunning) {
              // Allow for the server clock running a little behind this one.
              const since = startedAt - 60000;
              const outcome = platforms
                .map((p) =>
                  shares
                    .filter((s) => s.platform === p && new Date(s.sharedAt).getTime() >= since)
                    .sort((a, b) => new Date(b.sharedAt) - new Date(a.sharedAt))[0]
                )
                .filter(Boolean);
              if (outcome.length) {
                finish(outcome, 'Your connection dropped while publishing, but the server finished the job:');
              } else {
                finish(
                  null,
                  'Your connection dropped and the publish did not complete. It is safe to publish again.'
                );
              }
              return;
            }
          } catch {
            // Still no connection - keep trying.
          }
        }
        if (!cancelled) timer = setTimeout(poll, CHECK_EVERY_MS);
      }

      // The moment the connection is back is the moment to ask.
      window.addEventListener('online', poll);
      stopRef.current = () => {
        cancelled = true;
        clearTimeout(timer);
        window.removeEventListener('online', poll);
      };
      timer = setTimeout(poll, CHECK_EVERY_MS);
    },
    [queryClient, stopChecking]
  );

  const publish = useCallback(
    async (platforms, caption) => {
      if (!video?._id || publishing || checking) return;
      setResults(null);
      setNotice(null);

      if (isOffline()) {
        setNotice("You're offline. Connect to the internet, then press publish again.");
        return;
      }

      const startedAt = Date.now();
      setPublishing(true);
      try {
        const { data } = await axios.post(
          `${baseUrl}/api/videos/${video._id}/share`,
          { platforms, caption },
          // An upload takes minutes; no client-side time limit.
          { timeout: 0 }
        );
        setResults(data.data.results);
        queryClient.invalidateQueries('recordings');
      } catch (error) {
        if (!serverAnswered(error)) {
          // The server may well still be uploading - find out rather than guess.
          setNotice(
            'Your connection dropped while publishing. The server keeps going on its own - checking whether it finished…'
          );
          checkOutcome(video._id, platforms, startedAt);
        } else if (error.response.status === 409) {
          setNotice(`${error.response.data?.message || 'Already publishing.'} Checking on it…`);
          checkOutcome(video._id, platforms, startedAt);
        } else {
          setResults([
            {
              platform: platforms.join(', '),
              status: 'failed',
              error: error.response.data?.message || error.message,
            },
          ]);
        }
      } finally {
        setPublishing(false);
      }
    },
    [video, publishing, checking, queryClient, checkOutcome]
  );

  return { publish, publishing, checking, results, notice, reset, busy: publishing || checking };
}
